<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
// Public route — cannot import API_BASE_URL from appConfig without pulling in the
// full auth store. Inline detection keeps this view self-contained.
const { hostname } = window.location;
const API_BASE_URL = (() => {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000/api'
  }
  if (hostname === 'quotastic.ca') {
    return 'https://api.quotastic.ca/api'
  }
  return 'https://api-staging.quotastic.ca/api'
})()

const quote = ref(null);
const isLoading = ref(false);
const loadError = ref('');
const actionError = ref('');
const actionMessage = ref('');
const isSavingDecision = ref(false);

const lineItems = computed(() => quote.value?.line_items || []);
const subtotal = computed(() => Number(quote.value?.subtotal || 0));
const discount = computed(() => Number(quote.value?.discount || 0));
const taxAmount = computed(() => Number(quote.value?.tax_amount || 0));
const totalAmount = computed(() => Number(quote.value?.total_amount || 0));

const quoteStatus = computed(() => quote.value?.status || 'draft');
const isAccepted = computed(() => quoteStatus.value === 'accepted');
const isRejected = computed(() => quoteStatus.value === 'rejected');
const canDecide = computed(() =>
  ['draft', 'sent'].includes(quoteStatus.value)
);

const paymentRequiredType = computed(
  () => quote.value?.payment_required_type || 'full'
);
const depositPercent = computed(
  () => Number(quote.value?.deposit_percent || 0)
);
const depositAmount = computed(
  () => Number(quote.value?.deposit_amount || 0)
);
const balanceAmount = computed(
  () => Number(quote.value?.balance_amount || 0)
);

const paymentInstructions = computed(() =>
  normalizePaymentInstructions(quote.value?.payment_instructions)
);

const enabledPaymentMethods = computed(() => {
  const instructions = paymentInstructions.value;
  const methods = [];

  if (instructions.etransfer.enabled) {
    methods.push({
      id: 'etransfer',
      title: 'E-transfer',
      icon: 'mdi-email-fast-outline',
      lines: [
        { label: 'Email', value: instructions.etransfer.email },
        { label: 'Phone', value: instructions.etransfer.phone },
        { label: 'Recipient', value: instructions.etransfer.recipient_name },
      ].filter((line) => line.value),
    });
  }

  if (instructions.cheque.enabled) {
    methods.push({
      id: 'cheque',
      title: 'Cheque',
      icon: 'mdi-checkbook',
      lines: [
        { label: 'Payable to', value: instructions.cheque.payable_to },
        { label: 'Instructions', value: instructions.cheque.instructions },
      ].filter((line) => line.value),
    });
  }

  if (instructions.wire.enabled) {
    methods.push({
      id: 'wire',
      title: 'Wire transfer',
      icon: 'mdi-bank-outline',
      lines: [
        { label: 'Bank', value: instructions.wire.bank_name },
        { label: 'Account holder', value: instructions.wire.account_holder },
        { label: 'Institution', value: instructions.wire.institution_number },
        { label: 'Transit', value: instructions.wire.transit_number },
        { label: 'Account', value: instructions.wire.account_number },
        { label: 'Notes', value: instructions.wire.notes },
      ].filter((line) => line.value),
    });
  }

  return methods;
});

function formatMoney(value) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(value || 0);
}

function formatDate(value) {
  if (!value) return '';

  let date;

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function normalizePaymentInstructions(value) {
  const defaults = {
    etransfer: {
      enabled: false,
      email: '',
      phone: '',
      recipient_name: '',
    },
    cheque: {
      enabled: false,
      payable_to: '',
      instructions: '',
    },
    wire: {
      enabled: false,
      bank_name: '',
      institution_number: '',
      transit_number: '',
      account_number: '',
      account_holder: '',
      notes: '',
    },
  };

  if (!value) return defaults;

  let parsed = value;

  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value || '{}');
    } catch {
      return defaults;
    }
  }

  return {
    etransfer: { ...defaults.etransfer, ...parsed.etransfer },
    cheque: { ...defaults.cheque, ...parsed.cheque },
    wire: { ...defaults.wire, ...parsed.wire },
  };
}

function getQuoteLookupParams() {
  const params = new URLSearchParams();
  const id = route.query.id;
  const quoteNumber = route.query.quote_number || route.params.quoteNumber;

  if (id) {
    params.set('id', String(id));
  } else if (quoteNumber) {
    params.set('quote_number', String(quoteNumber));
  }

  return params;
}

async function loadQuote() {
  const params = getQuoteLookupParams();

  if (!params.toString()) {
    loadError.value = 'Quote link is missing.';
    return;
  }

  isLoading.value = true;
  loadError.value = '';
  actionError.value = '';
  actionMessage.value = '';

  try {
    const response = await fetch(`${API_BASE_URL}/invoice-quotes?${params}`);
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Unable to load quote.');
    }

    const rows = Array.isArray(data) ? data : data?.quotes || data?.data || [];
    quote.value = rows[0] || null;

    if (!quote.value) {
      throw new Error('Quote not found.');
    }
  } catch (error) {
    loadError.value = error.message || 'Unable to load quote.';
  } finally {
    isLoading.value = false;
  }
}

async function acceptQuote() {
  if (!quote.value?.id) return;

  isSavingDecision.value = true;
  actionError.value = '';
  actionMessage.value = '';

  try {
    const response = await fetch(
      `${API_BASE_URL}/invoice-quotes/${quote.value.id}/accept`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Unable to accept quote.');
    }

    quote.value = data?.quote || { ...quote.value, status: 'accepted' };
    actionMessage.value = 'Quote accepted. A copy has been emailed to you.';
  } catch (error) {
    actionError.value = error.message || 'Unable to accept quote.';
  } finally {
    isSavingDecision.value = false;
  }
}

async function updateQuoteStatus(status) {
  if (!quote.value?.id) return;

  isSavingDecision.value = true;
  actionError.value = '';
  actionMessage.value = '';

  try {
    const response = await fetch(
      `${API_BASE_URL}/invoice-quotes/${quote.value.id}/status`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, quote_number: quote.value.quote_number }),
      }
    );
    console.log('quote status updated: ',JSON.stringify({ status }))
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Unable to update quote.');
    }

    quote.value = data?.quote || { ...quote.value, status };
    actionMessage.value =
      status === 'accepted' ? 'Quote accepted.' : 'Quote declined.';
  } catch (error) {
    actionError.value = error.message || 'Unable to update quote.';
  } finally {
    isSavingDecision.value = false;
  }
}

onMounted(loadQuote);

watch(
  () => [route.params.quoteNumber, route.query.id, route.query.quote_number],
  loadQuote
);
</script>

<template>
  <main class="quote-preview">
    <section v-if="isLoading" class="state-screen">
      <v-progress-circular indeterminate color="#1F7A63" size="56" />
    </section>

    <section v-else-if="loadError" class="state-screen">
      <v-icon size="42" color="#C62828">mdi-alert-circle-outline</v-icon>
      <h1>Quote unavailable</h1>
      <p>{{ loadError }}</p>
    </section>

    <template v-else-if="quote">
      <section class="quote-header">
        <div class="brand-row">
          <strong>Quotera</strong>
          <span :class="['status-pill', `status-pill--${quoteStatus}`]">
            {{ quoteStatus }}
          </span>
        </div>

        <div class="quote-hero">
          <div>
            <p class="quote-number">{{ quote.quote_number }}</p>
            <h1>{{ quote.title || 'Project Quote' }}</h1>
            <p v-if="quote.description" class="quote-copy">
              {{ quote.description }}
            </p>
          </div>

          <div class="total-panel">
            <span>Total</span>
            <strong>{{ formatMoney(totalAmount) }}</strong>
          </div>
        </div>

        <div class="details-grid">
          <div>
            <span>Client</span>
            <strong>{{ quote.customer_name || 'Client' }}</strong>
            <p>{{ quote.customer_email }}</p>
            <p>{{ quote.customer_phone }}</p>
          </div>

          <div>
            <span>Job Address</span>
            <strong>{{ quote.job_address || 'Pending' }}</strong>
          </div>

          <div>
            <span>Quote Date</span>
            <strong>{{ formatDate(quote.quote_date) }}</strong>
          </div>

          <div>
            <span>Valid Until</span>
            <strong>{{ formatDate(quote.expiry_date) }}</strong>
          </div>
        </div>
      </section>

      <section class="quote-section">
        <div class="section-title">
          <v-icon size="22">mdi-format-list-bulleted</v-icon>
          <h2>Line Items</h2>
        </div>

        <div class="items-table">
          <div class="items-head">
            <span>Item</span>
            <span>Qty</span>
            <span>Unit</span>
            <span>Total</span>
          </div>

          <div
            v-for="item in lineItems"
            :key="item.id"
            class="item-row"
          >
            <div>
              <strong>{{ item.item_name }}</strong>
              <p>{{ item.description }}</p>
            </div>
            <span>{{ Number(item.quantity || 0) }}</span>
            <span>{{ formatMoney(Number(item.unit_price || 0)) }}</span>
            <strong>{{ formatMoney(Number(item.total_price || 0)) }}</strong>
          </div>

          <p v-if="!lineItems.length" class="empty-items">No line items.</p>
        </div>
      </section>

      <section class="quote-section summary-section">
        <div class="section-title">
          <v-icon size="22">mdi-calculator-variant-outline</v-icon>
          <h2>Summary</h2>
        </div>

        <div class="summary-list">
          <div>
            <span>Subtotal</span>
            <strong>{{ formatMoney(subtotal) }}</strong>
          </div>
          <div>
            <span>Discount</span>
            <strong>-{{ formatMoney(discount) }}</strong>
          </div>
          <div>
            <span>Tax</span>
            <strong>{{ formatMoney(taxAmount) }}</strong>
          </div>
          <div class="summary-total">
            <span>Total</span>
            <strong>{{ formatMoney(totalAmount) }}</strong>
          </div>
        </div>
      </section>

      <section class="quote-section decision-section">
        <div class="decision-copy">
          <h2 v-if="isAccepted">Quote accepted</h2>
          <h2 v-else-if="isRejected">Quote declined</h2>
          <h2 v-else>Ready to proceed?</h2>
          <p v-if="quote.notes">{{ quote.notes }}</p>
        </div>

        <div v-if="canDecide" class="decision-actions">
          <v-btn
            class="accept-btn"
            size="large"
            :loading="isSavingDecision"
            :disabled="isSavingDecision"
            @click="acceptQuote"
          >
            <v-icon start>mdi-check-circle-outline</v-icon>
            Accept Quote
          </v-btn>

          <v-btn
            variant="outlined"
            size="large"
            :disabled="isSavingDecision"
            @click="updateQuoteStatus('rejected')"
          >
            <v-icon start>mdi-close-circle-outline</v-icon>
            Decline
          </v-btn>
        </div>

        <p v-if="actionError" class="status-message status-message--error">
          {{ actionError }}
        </p>
        <p v-else-if="actionMessage" class="status-message">
          {{ actionMessage }}
        </p>
      </section>



      <section class="quote-section decision-section">
        <div class="decision-copy" v-if="isAccepted || isRejected">
          <h2  >Change Status</h2>
   
 
        </div>

        <div v-if="isAccepted || isRejected" class="decision-actions">
          <v-btn
            class="accept-btn"
            size="large"
            :loading="isSavingDecision"
            :disabled="isSavingDecision || isAccepted"
            @click="updateQuoteStatus('accepted')"
          >
            <v-icon start>mdi-check-circle-outline</v-icon>
            Accept Quote
          </v-btn>

          <v-btn
            variant="outlined"
            size="large"
            :disabled="isSavingDecision || isRejected"
            @click="updateQuoteStatus('rejected')"
          >
            <v-icon start>mdi-close-circle-outline</v-icon>
            Decline
          </v-btn>
        </div>

 
      </section>



      <section v-if="isAccepted" class="quote-section payment-section">
        <div class="section-title">
          <v-icon size="22">mdi-cash-multiple</v-icon>
          <h2>Payment</h2>
        </div>

        <div v-if="enabledPaymentMethods.length" class="payment-method-grid">
          <div
            v-for="method in enabledPaymentMethods"
            :key="method.id"
            class="payment-instruction-card"
          >
            <div class="payment-method-title">
              <v-icon size="22">{{ method.icon }}</v-icon>
              <strong>{{ method.title }}</strong>
            </div>

            <div v-if="method.lines.length" class="payment-lines">
              <div
                v-for="line in method.lines"
                :key="line.label"
              >
                <span>{{ line.label }}</span>
                <strong>{{ line.value }}</strong>
              </div>
            </div>

            <p v-else class="empty-payment">
              Contact the contractor for details.
            </p>
          </div>
        </div>

        <p v-else class="empty-payment">
          Payment instructions will be provided by the contractor.
        </p>

        <div class="payment-summary-card">
          <template v-if="paymentRequiredType === 'deposit'">
            <h3>Deposit Required</h3>

            <div class="payment-summary-row">
              <span>Deposit Percentage</span>
              <strong>{{ depositPercent }}%</strong>
            </div>

            <div class="payment-summary-row">
              <span>Deposit Due Today</span>
              <strong>{{ formatMoney(depositAmount) }}</strong>
            </div>

            <div class="payment-summary-row">
              <span>Remaining Balance</span>
              <strong>{{ formatMoney(balanceAmount) }}</strong>
            </div>
          </template>

          <template v-else>
            <h3>Full Payment Required</h3>

            <div class="payment-summary-row">
              <span>Amount Due</span>
              <strong>{{ formatMoney(totalAmount) }}</strong>
            </div>
          </template>
        </div>
      </section>
    </template>
  </main>
</template>








<style scoped>
.quote-preview {
  min-height: 100%;
  background: var(--surface-alt);
  color: var(--text-primary);
  padding: 24px 16px 80px;
}

.state-screen {
  min-height: 70vh;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 14px;
  text-align: center;
}

.state-screen h1 {
  font-size: 28px;
  margin: 0;
}

.state-screen p {
  margin: 0;
  color: var(--text-secondary);
}

.quote-header,
.quote-section {
  width: min(980px, 100%);
  margin: 0 auto 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
}

.quote-header {
  padding: 24px;
}

.brand-row,
.quote-hero,
.details-grid,
.section-title,
.decision-section,
.decision-actions {
  display: flex;
  align-items: center;
}

.brand-row {
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.brand-row strong {
  font-size: 20px;
}

.status-pill {
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 12px;
  background: #e8eefb;
  color: #1c4f94;
  font-size: 13px;
  font-weight: 700;
  text-transform: capitalize;
}

.status-pill--accepted {
  background: #e6f2ef;
  color: #145947;
}

.status-pill--rejected {
  background: #fdecec;
  color: #a32020;
}

.quote-hero {
  justify-content: space-between;
  gap: 28px;
  align-items: flex-start;
}

.quote-number {
  margin: 0 0 8px;
  color: #1f7a63;
  font-weight: 800;
}

.quote-hero h1 {
  margin: 0;
  font-size: clamp(30px, 5vw, 52px);
  line-height: 1.05;
}

.quote-copy {
  max-width: 660px;
  margin: 16px 0 0;
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
}

.total-panel {
  flex: 0 0 230px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 18px;
  background: var(--surface-alt);
}

.total-panel span,
.details-grid span,
.summary-list span {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.total-panel strong {
  display: block;
  margin-top: 6px;
  font-size: 30px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 28px;
}

.details-grid div {
  min-width: 0;
  border-top: 1px solid var(--border-subtle);
  padding-top: 14px;
}

.details-grid strong {
  display: block;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.details-grid p {
  margin: 5px 0 0;
  color: var(--text-secondary);
  overflow-wrap: anywhere;
}

.quote-section {
  padding: 20px;
}

.section-title {
  gap: 10px;
  margin-bottom: 16px;
}

.section-title h2,
.decision-copy h2 {
  margin: 0;
  font-size: 21px;
}

.items-table {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  overflow: hidden;
}

.items-head,
.item-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 80px 120px 120px;
  gap: 12px;
  align-items: center;
}

.items-head {
  background: var(--surface-alt);
  padding: 12px 14px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.item-row {
  padding: 14px;
  border-top: 1px solid var(--border-subtle);
}

.item-row p {
  margin: 4px 0 0;
  color: var(--text-secondary);
}

.empty-items {
  margin: 0;
  padding: 18px;
  color: var(--text-secondary);
}

.summary-section {
  display: grid;
  grid-template-columns: 1fr minmax(260px, 360px);
  gap: 24px;
}

.summary-list {
  display: grid;
  gap: 10px;
}

.summary-list div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid var(--border-subtle);
  padding-bottom: 10px;
}

.summary-list .summary-total {
  border-bottom: 0;
  padding-top: 6px;
}

.summary-total strong {
  font-size: 24px;
}

.decision-section {
  justify-content: space-between;
  gap: 18px;
}

.decision-copy {
  min-width: 0;
}

.decision-copy p {
  margin: 8px 0 0;
  color: var(--text-secondary);
}

.decision-actions {
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.accept-btn {
  background: #1f7a63 !important;
  color: #ffffff !important;
}

.status-message {
  width: 100%;
  margin: 12px 0 0;
  color: #145947;
  font-weight: 700;
}

.status-message--error {
  color: #c62828;
}

.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.payment-instruction-card {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface);
  padding: 16px;
}

.payment-method-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.payment-lines {
  display: grid;
  gap: 10px;
}

.payment-lines div {
  display: grid;
  gap: 3px;
}

.payment-lines span,
.empty-payment {
  color: var(--text-secondary);
}

.payment-lines strong {
  overflow-wrap: anywhere;
}

.empty-payment {
  margin: 0;
}

.payment-summary-card {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 18px;
  margin-top: 16px;
  background: var(--surface-alt);
}

.payment-summary-card h3 {
  margin: 0 0 14px;
}

.payment-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-subtle);
}

.payment-summary-row:last-child {
  border-bottom: 0;
}

@media (max-width: 760px) {
  .quote-preview {
    padding: 14px 10px 70px;
  }

  .quote-header,
  .quote-section {
    padding: 16px;
  }

  .quote-hero,
  .decision-section {
    display: grid;
  }

  .total-panel {
    width: 100%;
    flex-basis: auto;
  }

  .details-grid,
  .summary-section,
  .payment-method-grid {
    grid-template-columns: 1fr;
  }

  .items-head {
    display: none;
  }

  .item-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .decision-actions {
    justify-content: stretch;
  }

  .decision-actions .v-btn {
    width: 100%;
  }
}
</style>